import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and re-render when it changes. Mirrors the
 * pattern in {@link useReducedMotion}. SSR-safe: returns `false` when
 * `window`/`matchMedia` are unavailable.
 *
 * @example
 *   const isDesktop = useMediaQuery('(min-width: 768px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };
    // Sync immediately in case the query changed between render and effect.
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => {
      mql.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

/**
 * True from the Tailwind `md` breakpoint (768px) up — i.e. the viewport is
 * wide enough for the persistent desktop/tablet sidebar rail. Below this the
 * sidebar renders as an off-canvas drawer.
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

/**
 * True below the Tailwind `lg` breakpoint (1024px) — the cut where the app
 * swaps the desktop sidebar shell for the mobile bottom-tab shell
 * (android-app-plan §6.1). Desktop widths (`lg+`) are untouched by the
 * mobile workstream; phones and portrait tablets get MobileShell.
 */
export function useIsMobile(): boolean {
  return !useMediaQuery('(min-width: 1024px)');
}
